// Turns FortuneSheet's ops (the JSON-patch-like records the Workbook emits for
// every change) into the short edit log the Decode Agent reads with an edited
// canvas workbook, and counts them for the "unsent edits" state.
//
// Every op FortuneSheet emits for a change the person can see must count
// (X13-7): a first-time cell format arrives as an `add`, a removed one as a
// `remove`, and borders and hidden rows arrive as one `replace` of the sheet's
// whole `config`. Ops on view state (which sheet is active, the selection, the
// zoom) and the importer's own normalisation never count.
const PROP_LABELS = {
    bl: "Bold", it: "Italic", un: "Underline", cl: "Strikethrough", ff: "Font",
    bg: "Background", fc: "Font color", fs: "Font size",
    ht: "Horizontal align", vt: "Vertical align",
    tb: "Text wrap", tr: "Text rotation", ct: "Number format", mc: "Merge",
    hl: "Hyperlink", ps: "Comment",
};
const VALUE_DISPLAY_PROPS = new Set(["bg", "fc", "fs", "ht", "vt"]);
const STRUCTURAL_OPS = new Set(["insertRowCol", "deleteRowCol", "addSheet", "deleteSheet"]);
// The names of the structural ops that move cells: every cell after the
// change has a new row or column index.
const ROW_COL_OP_NAMES = new Set(["insertRowCol", "deleteRowCol"]);
const VALUE_PROPS = new Set(["v", "m", "f"]);
const FORMULA_SIDE_EFFECT_PATHS = new Set(["f", "calcChain"]);

// Per-sheet fields FortuneSheet changes as the person merely looks at the
// workbook (the active sheet, the selection, the zoom, the scroll), plus the
// derived state a real edit updates on its own (the formula calculation chain,
// the row and column counts). An op on one of these is never an edit.
const VIEW_STATE_ROOTS = new Set([
    "status", "luckysheet_select_save", "luckysheet_selection_range", "zoomRatio",
    "scrollLeft", "scrollTop", "calcChain", "dynamicArray_compute", "row", "column", "addRows",
]);
// Config flags FortuneSheet sets alongside a row or column resize to mark the
// size as hand-picked; the resize itself is what gets described.
const RESIZE_FLAG_CONFIG_KEYS = new Set(["customWidth", "customHeight"]);

const FROZEN_TYPE_LABELS = {
    row: "rows", rangeRow: "rows",
    column: "columns", rangeColumn: "columns",
    both: "rows and columns", rangeBoth: "rows and columns",
};

export default class OpAccumulator {
    constructor() {
        this.entries = [];
        // Per sheet id, the cells the canvas held when tracking began: the
        // "from" side of a cell's first edit. Keyed "row:col" by where the cell
        // is now, not where it was when tracking began.
        this.originalCells = new Map();
        this.sheetNames = {};
        // Per sheet id, what each cell touched since tracking began now holds,
        // keyed "row:col" for its value and "row:col:f" for its formula: the
        // "from" side of the cell's next edit. Both maps are keyed by where
        // the cell is now, so a row or column insert or delete re-keys them.
        this.lastKnownValues = new Map();
    }

    /**
     * Record the sheets as they stand as the baseline every cell's first edit
     * is described against. Called when the canvas loads a file and again
     * after a send, so the sheets arrive in either of FortuneSheet's shapes
     * (see baselineCells).
     */
    setOriginalSnapshot(sheetData) {
        this.originalCells = new Map();
        this.entries = [];
        this.sheetNames = {};
        this.lastKnownValues = new Map();
        for (const sheet of sheetData) {
            if (!sheet.id) continue;
            this.originalCells.set(sheet.id, baselineCells(sheet));
            if (sheet.name) {
                this.sheetNames[sheet.id] = sheet.name;
            }
        }
    }

    /**
     * Process one onOp callback payload.
     * @param {Op[]} ops - array of FortuneSheet ops from a single user action
     */
    ingest(ops) {
        if (!ops || ops.length === 0) return;
        // Whether some handler below described this batch. Kept apart from
        // the entry count: a description identical to the previous entry is
        // not appended, and that must not read as "nothing described it".
        let described = false;

        const structural = ops.filter(o => STRUCTURAL_OPS.has(o.op));
        const nonStructural = ops.filter(o => !STRUCTURAL_OPS.has(o.op));

        for (const op of structural) {
            described = true;
            this._push(this._formatStructuralOp(op));
        }

        // A row or column change moves the tracked cells before any cell op of
        // the batch is read: those ops already use the new row and column
        // indices.
        const rowColOps = structural.filter(o => ROW_COL_OP_NAMES.has(o.op));
        for (const rowColOp of rowColOps) {
            this._moveTrackedCells(rowColOp);
        }

        // Cell ops: a cell, or one property of a cell, being set or removed.
        const batchCellOps = nonStructural.filter(isCellOp);
        let cellOps;
        if (rowColOps.length > 0) {
            // The cell ops that come with a row or column change are
            // FortuneSheet's own consequences of it: formulas whose references
            // moved, merged ranges that shifted, and the style-only cells it
            // puts in rows inserted through a merged range. They are
            // remembered, so a later edit of such a cell is described against
            // what it holds now, but they are not the person's edits and are
            // not described. The one exception is content in the inserted rows
            // or columns themselves: undoing a delete arrives as an insert that
            // carries the deleted cells back.
            //
            // FortuneSheet emits a restored cell that holds a formula twice:
            // once among the rewritten formulas and once among the restored
            // cells. Described twice, the cell would be counted twice, and the
            // range the restored cells are described as would come out wrong.
            // So one op is kept per cell, the later one.
            const coalesced = coalesceValueOps(batchCellOps);
            const restoredOpByCell = new Map();
            for (const op of coalesced) {
                if (!isRestoredCellOp(op, rowColOps)) continue;
                const [, row, col] = op.path;
                restoredOpByCell.set(`${op.id}:${row}:${col}`, op);
            }
            cellOps = Array.from(restoredOpByCell.values());
            const describedOps = new Set(cellOps);
            for (const op of coalesced) {
                if (!describedOps.has(op)) this._rememberCellOp(op);
            }
        } else {
            // A structural change recalculates formulas as a side effect, and
            // those ops are not the person's edits.
            cellOps = coalesceValueOps(batchCellOps.filter(o => (
                structural.length === 0 || !FORMULA_SIDE_EFFECT_PATHS.has(o.path[3])
            )));
        }

        // The sheet-level ops that come with a row or column change are
        // FortuneSheet's bookkeeping for it as well: it rewrites the sheet's
        // merge map with every such change and removes it again on the undo.
        // Described, they told the agent "Merge config updated" after every
        // inserted row, and "Unmerged: range" after an undo that unmerged
        // nothing. The structural line already says what the person did.
        if (rowColOps.length === 0) {
            const cellOpSet = new Set(cellOps);
            for (const op of nonStructural) {
                if (cellOpSet.has(op)) continue;
                const entry = this._formatNonCellOp(op);
                if (!entry) continue;
                described = true;
                this._push(entry);
            }
        }

        if (cellOps.length === 1) {
            described = true;
            this._push(this._formatSingleOp(cellOps[0]));
        } else if (cellOps.length > 1) {
            described = true;
            for (const entry of this._compressBatch(cellOps)) {
                this._push(entry);
            }
        }

        // Nothing above described this batch, yet it changed something the
        // workbook keeps: say so in general terms rather than leave the
        // change invisible. Borders and hidden rows arrive this way, as one
        // replace of the sheet's whole config. A batch that was described
        // (a structural change and the config shifts it causes, a hyperlink
        // and its cell properties) gets no second, vaguer line.
        if (!described) {
            const undescribed = nonStructural.find(isWorkbookChange);
            if (undescribed) this._push(describeInGeneral(undescribed, this.sheetNames));
        }
    }

    /**
     * Return the compressed edit log, truncated if very large.
     * V1: naive cap — revisit with GCS offload + bounded inline summary if
     * long editing sessions or large pastes make this a real problem.
     * @returns {{ edits: string[], hasEdits: boolean }}
     */
    summarize() {
        const MAX_ENTRIES = 200;
        const all = this.entries;
        if (all.length <= MAX_ENTRIES) {
            return { edits: [...all], hasEdits: all.length > 0 };
        }
        const truncated = all.slice(0, MAX_ENTRIES);
        truncated.push(`... and ${all.length - MAX_ENTRIES} more edits (truncated)`);
        return { edits: truncated, hasEdits: true };
    }

    /** Reset accumulator state. */
    reset() {
        this.entries = [];
        this.originalCells = new Map();
        this.sheetNames = {};
        this.lastKnownValues = new Map();
    }

    /** @returns {boolean} whether any edits have been recorded */
    hasEdits() {
        return this.entries.length > 0;
    }

    /** @returns {number} how many edits have been recorded (untruncated) */
    editCount() {
        return this.entries.length;
    }

    // ── Private helpers ──────────────────────────────────────────────

    // A line identical to the one before it adds nothing: the same format
    // applied to the same range twice, or several ops of one action that all
    // describe as the same general change.
    _push(entry) {
        if (this.entries.length > 0 && this.entries[this.entries.length - 1] === entry) return;
        this.entries.push(entry);
    }

    _formatStructuralOp(op) {
        if (op.op === "insertRowCol") {
            const v = op.value;
            const pos = v.direction === "lefttop" ? "before" : "after";
            if (v.type === "row") {
                return `Inserted ${v.count} row(s) ${pos} row ${v.index + 1}`;
            } else {
                return `Inserted ${v.count} column(s) ${pos} column ${colToLetter(v.index)}`;
            }
        } else if (op.op === "deleteRowCol") {
            const v = op.value;
            if (v.type === "row") {
                return v.start === v.end
                    ? `Deleted row ${v.start + 1}`
                    : `Deleted rows ${v.start + 1} to ${v.end + 1}`;
            } else {
                return v.start === v.end
                    ? `Deleted column ${colToLetter(v.start)}`
                    : `Deleted columns ${colToLetter(v.start)} to ${colToLetter(v.end)}`;
            }
        } else if (op.op === "addSheet") {
            const name = op.value?.name || op.id || "unknown";
            // Track the new sheet so later renames/deletes can reference it
            if (op.id) this.sheetNames[op.id] = name;
            return `Added sheet "${name}"`;
        } else if (op.op === "deleteSheet") {
            const name = this.sheetNames[op.id] || op.value?.name || op.id || "unknown";
            return `Deleted sheet "${name}"`;
        }
        return `${op.op}: ${JSON.stringify(op.value)}`;
    }

    _formatSingleOp(op) {
        const [, r, c, prop] = op.path;
        const ref = cellRef(r, c);

        if (op.op === "remove") {
            if (!prop) {
                this._rememberCellOp(op);
                return `Cleared: ${ref}`;
            }
            if (VALUE_PROPS.has(prop)) return this._formatValueChange(op, null);
            return `Cleared ${PROP_LABELS[prop] || prop}: ${ref}`;
        }

        // Value change (cell-level or value property). A whole cell set with
        // no value holds nothing: the Delete key arrives this way, as the cell
        // replaced by an empty one, and so does a format applied to an empty
        // cell.
        if (!prop || VALUE_PROPS.has(prop)) {
            const newVal = !prop ? (op.value?.v ?? op.value?.m ?? null) : op.value;
            return this._formatValueChange(op, newVal);
        }

        // Format change
        const label = PROP_LABELS[prop] || prop;
        if (VALUE_DISPLAY_PROPS.has(prop)) {
            return `${label} ${formatPropValue(op.value)}: ${ref}`;
        }
        return `${label}: ${ref}`;
    }

    // Describe a cell's value (or formula) going from what was last known to
    // newVal, and remember newVal as the "from" side of the next change.
    _formatValueChange(op, newVal) {
        const [, r, c, prop] = op.path;
        const ref = cellRef(r, c);
        const oldVal = this._getPreviousValue(op.id, r, c, prop);
        this._rememberCellOp(op);
        const from = formatValue(oldVal);
        const to = formatValue(newVal);
        // The whole cell replaced with the same value: only its formatting
        // changed (a style applied to the cell object, or formatting cleared).
        // A cell that held nothing and still holds nothing is the same case.
        if (!prop && from === to) return `Formatting changed: ${ref}`;
        if (newVal === null) {
            return from ? `${ref}: ${from} → (cleared)` : `Cleared: ${ref}`;
        }
        return from ? `${ref}: ${from} → ${to}` : `${ref}: → ${to}`;
    }

    _compressBatch(ops) {
        const groups = new Map();

        for (const op of ops) {
            const [, row, col, prop] = op.path;
            let groupKey;
            if (!prop && op.op === "remove") {
                groupKey = "__cell_clear__";
            } else if (!prop) {
                // A whole cell set. One that leaves no value and no formula is
                // the Delete key if the cell held something (FortuneSheet
                // replaces the cell with an empty one), and a format applied
                // to an empty cell if it held nothing.
                const cell = op.value;
                const leavesContent = cell?.v != null || cell?.m != null || cell?.f != null;
                const heldContent = this._getPreviousValue(op.id, row, col) !== null
                    || this._getPreviousValue(op.id, row, col, "f") !== null;
                if (leavesContent) {
                    groupKey = "__cell_replace__";
                } else if (heldContent) {
                    groupKey = "__cell_clear__";
                } else {
                    groupKey = "__cell_format__";
                }
            } else if (VALUE_PROPS.has(prop)) {
                groupKey = "__value_edit__";
            } else if (op.op === "remove") {
                groupKey = `__remove__:${prop}`;
            } else {
                groupKey = `${prop}:${JSON.stringify(op.value)}`;
            }
            if (!groups.has(groupKey)) groups.set(groupKey, []);
            groups.get(groupKey).push(op);
        }

        const entries = [];
        for (const [key, groupOps] of groups) {
            const cells = groupOps.map(o => ({ row: o.path[1], col: o.path[2] }));
            const range = cellsToRange(cells);

            // A paste or a range clear is described as one range, but each of
            // its cells is remembered: a later edit of one of them must be
            // described against what the paste left there.
            if (key === "__cell_replace__") {
                groupOps.forEach(op => this._rememberCellOp(op));
                entries.push(`Updated values: ${range}`);
            } else if (key === "__cell_clear__") {
                groupOps.forEach(op => this._rememberCellOp(op));
                entries.push(`Cleared: ${range}`);
            } else if (key === "__cell_format__") {
                groupOps.forEach(op => this._rememberCellOp(op));
                entries.push(`Formatting changed: ${range}`);
            } else if (key === "__value_edit__") {
                for (const op of groupOps) {
                    entries.push(this._formatSingleOp(op));
                }
            } else if (key.startsWith("__remove__:")) {
                const prop = key.slice("__remove__:".length);
                entries.push(`Cleared ${PROP_LABELS[prop] || prop}: ${range}`);
            } else {
                const [prop, valJson] = [key.split(":")[0], key.slice(key.indexOf(":") + 1)];
                const label = PROP_LABELS[prop] || prop;
                if (VALUE_DISPLAY_PROPS.has(prop)) {
                    entries.push(`${label} ${formatPropValue(JSON.parse(valJson))}: ${range}`);
                } else {
                    entries.push(`${label}: ${range}`);
                }
            }
        }
        return entries;
    }

    /** Look up the previous value for a cell: what it was last known to hold
     *  first, then the baseline. When prop is "f" (formula), reads the cell's
     *  formula instead of its value. */
    _getPreviousValue(sheetId, row, col, prop) {
        const lastKnown = this.lastKnownValues.get(sheetId);
        const key = trackedCellKey(row, col, prop);
        if (lastKnown?.has(key)) {
            return lastKnown.get(key);
        }
        const cell = this.originalCells.get(sheetId)?.get(`${row}:${col}`);
        if (!cell) return null;
        if (prop === "f") return cell.f ?? null;
        return cell.v ?? cell.m ?? null;
    }

    // Remember what a cell op leaves in its cell: the "from" side of that
    // cell's next edit. An op on the whole cell sets, or clears, both its value
    // and its formula; an op on one value property sets that property alone.
    // An op on anything else (a format, a merge) changes neither.
    _rememberCellOp(op) {
        const [, row, col, prop] = op.path;
        if (!this.lastKnownValues.has(op.id)) this.lastKnownValues.set(op.id, new Map());
        const lastKnown = this.lastKnownValues.get(op.id);
        const removed = op.op === "remove";
        if (!prop) {
            const cell = removed ? null : op.value;
            lastKnown.set(trackedCellKey(row, col, "v"), cell?.v ?? cell?.m ?? null);
            lastKnown.set(trackedCellKey(row, col, "f"), cell?.f ?? null);
        } else if (VALUE_PROPS.has(prop)) {
            lastKnown.set(trackedCellKey(row, col, prop), removed ? null : op.value);
        }
    }

    // Re-key one sheet's tracked cells (the baseline and the last known values)
    // to where a row or column insert or delete has put them. Cells in deleted
    // rows or columns are forgotten. Without this, every later edit below an
    // inserted row was described against the value of the cell that used to
    // have its index, which is a different cell (bug 15.5).
    _moveTrackedCells(rowColOp) {
        const sheetId = rowColOp.id;
        const baseline = this.originalCells.get(sheetId);
        if (baseline) this.originalCells.set(sheetId, movedCells(baseline, rowColOp));
        const lastKnown = this.lastKnownValues.get(sheetId);
        if (lastKnown) this.lastKnownValues.set(sheetId, movedCells(lastKnown, rowColOp));
    }

    // The ops with a known shape that are not cell ops. Returns null for any
    // other op; ingest decides whether that op still counts as a change.
    _formatNonCellOp(op) {
        const p = op.path;
        const sets = op.op === "replace" || op.op === "add";

        // A whole row of cells: replace on ["data", r]. Each cell of the row is
        // remembered, as for a paste.
        if (sets && p.length === 2 && p[0] === "data") {
            const row = Number(p[1]);
            (op.value || []).forEach((cell, col) => {
                this._rememberCellOp({ op: "replace", id: op.id, path: ["data", row, col], value: cell });
            });
            return `Updated row ${row + 1}`;
        }

        // Sheet rename: replace on ["name"]
        if (sets && p.length === 1 && p[0] === "name") {
            const oldName = this.sheetNames[op.id] || op.id;
            // Update the map so subsequent renames/deletes use the current name
            if (op.id) this.sheetNames[op.id] = op.value;
            return `Renamed sheet "${oldName}" to "${op.value}"`;
        }

        // Row height: replace on ["config", "rowlen", row]
        if (sets && p[0] === "config" && p[1] === "rowlen" && p.length === 3) {
            return `Resized row ${Number(p[2]) + 1} height`;
        }

        // Column width: replace on ["config", "columnlen", col]
        if (sets && p[0] === "config" && p[1] === "columnlen" && p.length === 3) {
            return `Resized column ${colToLetter(Number(p[2]))} width`;
        }

        // Merge config: replace on ["config", "merge", ...]
        if (sets && p[0] === "config" && p[1] === "merge") {
            if (op.value && typeof op.value === "object" && "r" in op.value) {
                const { r, c, rs = 1, cs = 1 } = op.value;
                return `Merged: ${cellRef(r, c)}:${cellRef(r + rs - 1, c + cs - 1)}`;
            }
            return `Merge config updated`;
        }

        // Unmerge: remove on ["config", "merge", key]
        if (op.op === "remove" && p[0] === "config" && p[1] === "merge") {
            return `Unmerged: ${p[2] || "range"}`;
        }

        // Freeze panes: set on ["frozen"]; unfreeze: remove on ["frozen"]
        if (p.length === 1 && p[0] === "frozen") {
            if (op.op === "remove") return "Unfroze panes";
            return `Froze ${FROZEN_TYPE_LABELS[op.value?.type] || "panes"}`;
        }

        // Sheet order: replace on ["order"]
        if (sets && p[0] === "order") {
            return `Reordered sheets`;
        }

        return null;
    }
}

// ── Standalone helpers ───────────────────────────────────────────────

// A cell, one property of a cell, or a field inside one: ["data", row, col],
// ["data", row, col, prop] or ["data", row, col, prop, ...]. FortuneSheet
// changes an existing number format at ["data", row, col, "ct", "fa"].
function isCellOp(op) {
    return op.path[0] === "data" && op.path.length >= 3;
}

// The key a cell is tracked under within its sheet: "row:col" for its value
// (the `v` and `m` properties describe one value) and "row:col:f" for its formula.
function trackedCellKey(row, col, prop) {
    return prop === "f" ? `${row}:${col}:f` : `${row}:${col}`;
}

// The first and last row (or column) index an insert created. FortuneSheet
// names the row the insert was made at and a side: "lefttop" puts the new rows
// at that index, "rightbottom" puts them after it.
function insertedSpan(insertOp) {
    const { index, count, direction } = insertOp.value;
    const first = index + (direction === "rightbottom" ? 1 : 0);
    return { first, last: first + count - 1 };
}

// Where row (or column) `index` is after a row or column change, or null if
// the change deleted it. A delete names its first and last index, inclusive.
function indexAfterRowColOp(rowColOp, index) {
    if (rowColOp.op === "insertRowCol") {
        const { first } = insertedSpan(rowColOp);
        return index >= first ? index + rowColOp.value.count : index;
    }
    const { start, end } = rowColOp.value;
    if (index < start) return index;
    if (index <= end) return null;
    return index - (end - start + 1);
}

// One sheet's tracked cells, re-keyed to where a row or column change has put
// them. Keys are "row:col", or "row:col:f" for a formula; the suffix moves
// with its cell.
function movedCells(cells, rowColOp) {
    const movesRows = rowColOp.value.type === "row";
    const moved = new Map();
    for (const [key, tracked] of cells) {
        const formulaSuffix = key.endsWith(":f") ? ":f" : "";
        const [row, col] = key.split(":");
        const movedRow = movesRows ? indexAfterRowColOp(rowColOp, Number(row)) : Number(row);
        const movedCol = movesRows ? Number(col) : indexAfterRowColOp(rowColOp, Number(col));
        if (movedRow === null || movedCol === null) continue;
        moved.set(`${movedRow}:${movedCol}${formulaSuffix}`, tracked);
    }
    return moved;
}

// Whether a cell op that came with a row or column change puts content back
// into the rows or columns an insert created: a whole cell, set, holding a
// value or a formula. That is the undo of a delete restoring what it removed.
// A style-only cell in the same place is FortuneSheet's own filler.
//
// FortuneSheet writes the restored cells from the insert's own index, and the
// insert an undo makes is always "lefttop" (its inverseRowColOptions), for
// which insertedSpan starts at that same index. An op for another sheet is
// never a restored cell: FortuneSheet stamps the restored cells with the sheet
// on screen, so an undo made after switching sheets carries that sheet's cells.
function isRestoredCellOp(op, rowColOps) {
    if (op.op === "remove" || op.path.length !== 3) return false;
    const cell = op.value;
    const hasContent = cell != null && (cell.v != null || cell.m != null || cell.f != null);
    if (!hasContent) return false;
    const [, row, col] = op.path;
    return rowColOps.some(rowColOp => {
        if (rowColOp.op !== "insertRowCol" || rowColOp.id !== op.id) return false;
        const { first, last } = insertedSpan(rowColOp);
        const index = rowColOp.value.type === "row" ? row : col;
        return index >= first && index <= last;
    });
}

// FortuneSheet records one typed value as several ops on the cell: its value
// `v`, its display text `m`, and often the number format `ct` it inferred
// from what was typed. One entry per cell describes that edit; the display
// text and the inferred format are not edits of their own. A number format
// applied on its own arrives as `ct` with a recomputed `m` and no `v`, and
// is described as the format change it is.
function coalesceValueOps(ops) {
    const cellKey = (op) => `${op.id}:${op.path[1]}:${op.path[2]}`;
    const cellsWithValueOp = new Set(ops.filter(o => o.path[3] === "v").map(cellKey));
    const cellsWithFormatOp = new Set(ops.filter(o => o.path[3] === "ct").map(cellKey));
    return ops.filter(op => {
        const prop = op.path[3];
        if (prop === "m") return !cellsWithValueOp.has(cellKey(op)) && !cellsWithFormatOp.has(cellKey(op));
        if (prop === "ct") return !cellsWithValueOp.has(cellKey(op));
        return true;
    });
}

// Whether an op with no specific description still changed something the
// workbook keeps, as opposed to view state or the importer's normalisation.
function isWorkbookChange(op) {
    const root = op.path[0];
    if (root === undefined) return false;
    if (VIEW_STATE_ROOTS.has(root)) return false;
    if (root === "config" && RESIZE_FLAG_CONFIG_KEYS.has(op.path[1])) return false;
    // A moment after a file loads, the importer adds the row-height and
    // column-width maps the sheet lacked, as empty objects: nothing changed.
    // Only an `add` is that normalisation; a `replace` that leaves a map empty
    // (unhiding the last hidden row) is a change.
    if (op.op === "add" && isOnlyEmptyContainers(op.value)) return false;
    return true;
}

function isOnlyEmptyContainers(value) {
    if (Array.isArray(value)) return value.length === 0;
    if (value !== null && typeof value === "object") {
        return Object.values(value).every(isOnlyEmptyContainers);
    }
    return false;
}

function describeInGeneral(op, sheetNames) {
    const sheetName = sheetNames[op.id] || op.id || "unknown";
    if (op.path[0] === "data") return `Updated values on sheet "${sheetName}"`;
    return `Non-content change to sheet "${sheetName}" (likely formatting) — check if relevant`;
}

// One sheet's cells keyed "row:col", from whichever shape the sheet is in.
// The importer lists the cells it read as a sparse `celldata` ([{r, c, v}]),
// which is what the canvas hands over when a file loads; the workbook expands
// that into the 2-D `data` matrix its ops address and getAllSheets() returns,
// with the same row and column indices. Only the value, display text and
// formula are kept: they are all the previous-value lookup reads.
function baselineCells(sheet) {
    const cells = new Map();
    const keep = (row, col, cell) => {
        if (!cell) return;
        cells.set(`${row}:${col}`, typeof cell === "object"
            ? { v: cell.v, m: cell.m, f: cell.f }
            : { v: cell });
    };
    if (Array.isArray(sheet.data)) {
        sheet.data.forEach((rowCells, row) => {
            (rowCells || []).forEach((cell, col) => keep(row, col, cell));
        });
    } else if (Array.isArray(sheet.celldata)) {
        sheet.celldata.forEach(({ r, c, v }) => keep(r, c, v));
    }
    return cells;
}

function cellRef(row, col) {
    return `${colToLetter(col)}${row + 1}`;
}

function colToLetter(col) {
    let result = "";
    let c = col;
    while (c >= 0) {
        result = String.fromCharCode(65 + (c % 26)) + result;
        c = Math.floor(c / 26) - 1;
    }
    return result;
}

function cellsToRange(cells) {
    if (cells.length === 0) return "";
    if (cells.length === 1) return cellRef(cells[0].row, cells[0].col);

    const minRow = Math.min(...cells.map(c => c.row));
    const maxRow = Math.max(...cells.map(c => c.row));
    const minCol = Math.min(...cells.map(c => c.col));
    const maxCol = Math.max(...cells.map(c => c.col));

    const expectedCount = (maxRow - minRow + 1) * (maxCol - minCol + 1);
    if (cells.length === expectedCount) {
        if (minRow === maxRow && minCol === maxCol) return cellRef(minRow, minCol);
        return `${cellRef(minRow, minCol)}:${cellRef(maxRow, maxCol)}`;
    }
    if (minRow === maxRow) return cells.map(c => cellRef(c.row, c.col)).join(", ");
    if (minCol === maxCol) return cells.map(c => cellRef(c.row, c.col)).join(", ");
    return `~${cellRef(minRow, minCol)}:${cellRef(maxRow, maxCol)} (${cells.length} cells)`;
}

function formatValue(val) {
    if (val === null || val === undefined) return null;
    if (typeof val === "string") return `"${val}"`;
    return String(val);
}

function formatPropValue(val) {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    return String(val);
}
